import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import React, { useState } from 'react';

import type { KubernetesConsoleApi } from './api';
import type { AlertmanagerMatcher } from './types';

const DURATION_PRESETS: { label: string; seconds: number }[] = [
  { label: '30 minutes', seconds: 1800 },
  { label: '1 hour', seconds: 3600 },
  { label: '2 hours', seconds: 7200 },
  { label: '6 hours', seconds: 21600 },
  { label: '12 hours', seconds: 43200 },
  { label: '1 day', seconds: 86400 },
  { label: '2 days', seconds: 172800 },
  { label: '1 week', seconds: 604800 },
];

const MATCHER_NAME_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/;

function emptyMatcher(): AlertmanagerMatcher {
  return { name: '', value: '', isRegex: false, isEqual: true };
}

export interface SilenceFormPrefill {
  matchers?: AlertmanagerMatcher[];
  comment?: string;
}

export function SilenceForm({
  api,
  cluster,
  namespace,
  open,
  prefill,
  maxDurationSeconds,
  onClose,
  onCreated,
}: {
  api: KubernetesConsoleApi;
  cluster: string;
  namespace: string;
  open: boolean;
  prefill?: SilenceFormPrefill;
  maxDurationSeconds?: number;
  onClose: () => void;
  onCreated: (result: { silenceId: string; link?: string }) => void;
}) {
  const [matchers, setMatchers] = useState<AlertmanagerMatcher[]>([]);
  const [comment, setComment] = useState('');
  const [durationSeconds, setDurationSeconds] = useState(3600);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-seed local state whenever the dialog is (re)opened with new prefill
  // data — Dialog stays mounted so props alone won't reset form state.
  const openSignature = `${open}:${prefill?.comment ?? ''}:${(
    prefill?.matchers ?? []
  )
    .map(m => `${m.name}=${m.value}`)
    .join(',')}`;
  const [seededFor, setSeededFor] = useState('');
  if (open && seededFor !== openSignature) {
    setSeededFor(openSignature);
    setMatchers(
      prefill?.matchers && prefill.matchers.length > 0
        ? prefill.matchers.map(m => ({ ...m }))
        : [emptyMatcher()],
    );
    setComment(prefill?.comment ?? '');
    setDurationSeconds(
      maxDurationSeconds ? Math.min(3600, maxDurationSeconds) : 3600,
    );
    setError(null);
  }

  const presets = maxDurationSeconds
    ? DURATION_PRESETS.filter(preset => preset.seconds <= maxDurationSeconds)
    : DURATION_PRESETS;

  const updateMatcher = (
    index: number,
    patch: Partial<AlertmanagerMatcher>,
  ) => {
    setMatchers(current =>
      current.map((matcher, i) =>
        i === index ? { ...matcher, ...patch } : matcher,
      ),
    );
  };

  const removeMatcher = (index: number) => {
    setMatchers(current => current.filter((_, i) => i !== index));
  };

  const validate = (): string | null => {
    if (comment.trim().length < 3 || comment.trim().length > 500) {
      return 'Comment must be between 3 and 500 characters.';
    }
    if (matchers.length === 0 || matchers.length > 20) {
      return 'A silence needs between 1 and 20 matchers.';
    }
    for (const matcher of matchers) {
      if (!MATCHER_NAME_PATTERN.test(matcher.name)) {
        return `Matcher name "${matcher.name}" must start with a letter or underscore and contain only letters, digits and underscores.`;
      }
      if (!matcher.value || matcher.value.length > 512) {
        return `Matcher "${matcher.name}" needs a value up to 512 characters.`;
      }
    }
    return null;
  };

  const submit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await api.createSilence(cluster, {
        namespace,
        comment: comment.trim(),
        durationSeconds,
        matchers,
      });
      onCreated(result);
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : String(submitError),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create silence</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Typography sx={{ color: '#c7c7c7', fontSize: 12 }}>
            Matchers select which alerts this silence applies to. The{' '}
            <code>namespace</code> and <code>cluster</code> matchers are added
            automatically — only add them yourself if the value exactly matches
            this namespace/cluster.
          </Typography>
          <Stack spacing={1.5}>
            {matchers.map((matcher, index) => (
              <Stack
                key={index}
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ sm: 'center' }}
              >
                <TextField
                  size="small"
                  label="Label"
                  value={matcher.name}
                  onChange={event =>
                    updateMatcher(index, { name: event.target.value })
                  }
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  label="Value"
                  value={matcher.value}
                  onChange={event =>
                    updateMatcher(index, { value: event.target.value })
                  }
                  sx={{ flex: 1 }}
                />
                <FormControlLabel
                  sx={{ mr: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={matcher.isRegex}
                      onChange={event =>
                        updateMatcher(index, { isRegex: event.target.checked })
                      }
                    />
                  }
                  label={<Typography sx={{ fontSize: 12 }}>Regex</Typography>}
                />
                <FormControlLabel
                  sx={{ mr: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={matcher.isEqual === false}
                      onChange={event =>
                        updateMatcher(index, {
                          isEqual: !event.target.checked,
                        })
                      }
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: 12 }}>Negative (≠)</Typography>
                  }
                />
                <IconButton
                  size="small"
                  onClick={() => removeMatcher(index)}
                  disabled={matchers.length === 1}
                  aria-label="Remove matcher"
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() =>
                setMatchers(current => [...current, emptyMatcher()])
              }
              sx={{ alignSelf: 'flex-start' }}
            >
              Add matcher
            </Button>
          </Stack>
          <TextField
            select
            size="small"
            label="Duration"
            value={durationSeconds}
            onChange={event => setDurationSeconds(Number(event.target.value))}
            sx={{ width: 220 }}
          >
            {presets.map(preset => (
              <MenuItem key={preset.seconds} value={preset.seconds}>
                {preset.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Comment"
            required
            multiline
            minRows={2}
            value={comment}
            onChange={event => setComment(event.target.value)}
            helperText={`${comment.trim().length}/500 characters (minimum 3)`}
            fullWidth
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={submitting}>
          {submitting ? 'Creating…' : 'Create silence'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function matchersFromLabels(
  labels: Record<string, string>,
): AlertmanagerMatcher[] {
  return Object.entries(labels)
    .filter(([name]) => name !== 'namespace' && name !== 'cluster')
    .map(([name, value]) => ({ name, value, isRegex: false, isEqual: true }));
}

// Silences don't have an "edit" API — recreating one is the supported model
// (mirrors the real console's edit-is-expire-and-recreate flow).
export function prefillFromSilence(silence: {
  matchers: AlertmanagerMatcher[];
  comment?: string;
}): SilenceFormPrefill {
  return {
    matchers: silence.matchers.map(m => ({ ...m })),
    comment: silence.comment,
  };
}
